import { Component, EventEmitter, Input, Output, ChangeDetectionStrategy, OnInit, OnDestroy } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { CardapioEstabelecimentoDTO } from '../../../../models/cardapio-digital/cardapio-digital.model';

/** Horario parseado de um dia da semana */
interface HorarioDia {
  dia: string;
  horario: string;
  fechado: boolean;
  atual: boolean;
}

@Component({
  selector: 'app-cardapio-sidebar',
  templateUrl: './cardapio-sidebar.component.html',
  styleUrls: ['./cardapio-sidebar.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CardapioSidebarComponent implements OnInit, OnDestroy {
  @Input() visible = false;
  @Input() estabelecimento!: CardapioEstabelecimentoDTO;
  @Input() temHistorico = false;
  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() verHistorico = new EventEmitter<void>();

  horarios: HorarioDia[] = [];

  // Textos traduzidos
  texts: { [key: string]: string } = {};

  private langSub?: Subscription;

  // Mapa de nomes de dias (backend pode retornar pt-BR abreviado ou espanhol completo) para chave interna
  private diasAliasMap: Record<string, string> = {
    // Portugues abreviado (formato atual do backend CardapioViewMapper)
    'seg': 'seg', 'ter': 'ter', 'qua': 'qua', 'qui': 'qui', 'sex': 'sex', 'sab': 'sab', 'dom': 'dom',
    // Espanhol completo
    'lunes': 'seg', 'martes': 'ter', 'miercoles': 'qua', 'miércoles': 'qua',
    'jueves': 'qui', 'viernes': 'sex', 'sabado': 'sab', 'sábado': 'sab', 'domingo': 'dom',
    // Portugues completo
    'segunda': 'seg', 'terca': 'ter', 'terça': 'ter', 'quarta': 'qua',
    'quinta': 'qui', 'sexta': 'sex',
  };

  // Indice JS do dia da semana (0=domingo) para cada chave interna
  private diasIndexMap: Record<string, number> = {
    'dom': 0, 'seg': 1, 'ter': 2, 'qua': 3, 'qui': 4, 'sex': 5, 'sab': 6,
  };

  // Ordem dos dias para exibicao (segunda a domingo)
  private diasOrdem = ['seg', 'ter', 'qua', 'qui', 'sex', 'sab', 'dom'];

  constructor(private translate: TranslateService) {}

  ngOnInit(): void {
    this.loadTranslations();
    this.langSub = this.translate.onLangChange.subscribe(() => {
      this.loadTranslations();
      this.parseHorarios();
    });
  }

  ngOnDestroy(): void {
    this.langSub?.unsubscribe();
  }

  onShow(): void {
    this.parseHorarios();
  }

  onHide(): void {
    this.visibleChange.emit(false);
  }

  onVerHistorico(): void {
    this.visibleChange.emit(false); // Fecha sidebar
    this.verHistorico.emit();
  }

  /** Retorna link do WhatsApp */
  getWhatsappLink(): string {
    return `https://wa.me/${this.estabelecimento?.telefoneWhatsapp}`;
  }

  /** Retorna link do Instagram */
  getInstagramLink(): string {
    return `https://instagram.com/${this.estabelecimento?.perfilInstagram}`;
  }

  /** Retorna link do Facebook */
  getFacebookLink(): string {
    return `https://facebook.com/${this.estabelecimento?.perfilFacebook}`;
  }

  /** Parsea a string horarioFuncionamento em lista de dias */
  parseHorarios(): void {
    const raw = this.estabelecimento?.horarioFuncionamento;
    const hoje = new Date().getDay(); // 0=domingo
    const fechadoLabel = this.translate.instant('cardapio_digital.sidebar.fechado_label');

    // Mapa de chave interna -> horario(s)
    const horarioMap: Record<string, string> = {};

    if (raw) {
      // Parsear string "Seg: 08:00 - 18:00 | Ter: 08:00 - 18:00 | ..."
      const partes = raw.split('|').map(p => p.trim());

      for (const parte of partes) {
        const separador = parte.indexOf(':');
        if (separador === -1) continue;
        const diaRaw = parte.substring(0, separador).trim().toLowerCase();
        const horario = parte.substring(separador + 1).trim();
        // Normalizar: remover acentos
        const diaNorm = diaRaw.replace(/[áàã]/g, 'a').replace(/[éè]/g, 'e').replace(/[íì]/g, 'i').replace(/[óò]/g, 'o').replace(/[úù]/g, 'u');
        // Mapear para chave interna (seg, ter, qua, ...)
        const chave = this.diasAliasMap[diaNorm];
        if (chave) {
          // Se ja tem horario, concatenar (ex: dois turnos)
          horarioMap[chave] = horarioMap[chave] ? `${horarioMap[chave]}, ${horario}` : horario;
        }
      }
    }

    // Monta lista dos 7 dias na ordem correta
    this.horarios = this.diasOrdem.map(diaKey => {
      const horarioStr = horarioMap[diaKey];
      const diaIndex = this.diasIndexMap[diaKey] ?? -1;
      const isAtual = diaIndex === hoje;
      const isFechado = !horarioStr || horarioStr.toLowerCase() === 'cerrado' || horarioStr.toLowerCase() === 'fechado';
      const diaTraducao = this.translate.instant(`cardapio_digital.sidebar.dias.${diaKey}`);

      return {
        dia: diaTraducao,
        horario: isFechado ? fechadoLabel : horarioStr,
        fechado: isFechado,
        atual: isAtual,
      };
    });
  }

  private loadTranslations(): void {
    const keys = [
      'cardapio_digital.sidebar.titulo',
      'cardapio_digital.sidebar.endereco_titulo',
      'cardapio_digital.sidebar.tipos_servico_titulo',
      'cardapio_digital.sidebar.horarios_titulo',
      'cardapio_digital.sidebar.entrega',
      'cardapio_digital.sidebar.retirada',
      'cardapio_digital.sidebar.tempo_entrega',
      'cardapio_digital.sidebar.fechado_label',
      'cardapio_digital.sidebar.versao',
    ];

    const values = this.translate.instant(keys);
    this.texts = {};
    keys.forEach((k, i) => {
      const shortKey = k.replace('cardapio_digital.sidebar.', '');
      this.texts[shortKey] = values[k] || k;
    });
  }
}
